import { ApiError } from '../../../../lib/apiError';
import type { FilesApi } from '../casesApi';

/**
 * Files each picked file against a record that now exists — ONE request per file, in order.
 * Routing's `fileAll`, for a case or a report.
 *
 * **After the record, never with it.** A file that fails does not undo the case or the report —
 * resubmitting the form would write it twice. What failed is RETURNED, by name, and the reader
 * attaches it again from the record's Attachments tab.
 *
 * `onProgress` reports the real figure across the whole batch, because `apiUpload` measures it.
 */
export async function fileAll(
  files: FilesApi,
  picked: File[],
  onProgress: (percent: number) => void,
): Promise<{ name: string; reason: string }[]> {
  const failed: { name: string; reason: string }[] = [];

  for (const [index, file] of picked.entries()) {
    try {
      await files.attach(file, (percent) => onProgress(Math.floor(((index + percent / 100) / picked.length) * 100)));
    } catch (caught) {
      failed.push({
        name: file.name,
        reason: caught instanceof ApiError ? caught.message : 'It could not be filed.',
      });
    }
  }

  onProgress(100);

  return failed;
}

/** The sentence a form shows for what did not file — the record itself was saved. */
export function failedFilesMessage(failed: { name: string; reason: string }[]): string {
  return failed.length === 1
    ? `${failed[0].name} was not attached: ${failed[0].reason} Attach it again from the Attachments tab.`
    : `${failed.length} files were not attached (${failed.map((f) => f.name).join(', ')}). Attach them again from the Attachments tab.`;
}
